import { useState } from "react"
import { useForm } from "@/lib/use-form"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Field, FieldDescription, FieldGroup } from "@/components/ui/field"
import { LandingPage } from "@/components/landing-page"
import { TermsCheckbox } from "@/components/terms-checkbox"
import { SsoButtons } from "@/components/sso-buttons"
import type { LandingUsersInvitationEditProps } from "@/types/generated"

const InvitationEditPage = ({
  user,
  organizationName,
  inviterEmail,
  ssoProviders
}: LandingUsersInvitationEditProps) => {
  const { t } = useTranslation()
  const [termsAccepted, setTermsAccepted] = useState(false)

  const form = useForm({
    "user[invitation_token]": user.invitationToken,
    "user[password]": "",
    "user[password_confirmation]": ""
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!termsAccepted) return
    form.put("/users/invitation", { forceFormData: true })
  }

  const allErrors = Object.values(user.errors ?? {}).flat()
  const hasSso = ssoProviders.google || ssoProviders.microsoft || ssoProviders.openidConnect

  const termsCheckbox = <TermsCheckbox accepted={termsAccepted} onAcceptedChange={setTermsAccepted} />

  return (
    <LandingPage organizationName={organizationName}>
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold">
            {t("invitations.edit.welcome", { organization_name: organizationName })}
          </h1>
          {inviterEmail && (
            <p className="text-muted-foreground text-sm">{t("invitations.edit.invited_by", { email: inviterEmail })}</p>
          )}
        </div>

        {allErrors.length > 0 && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <ul className="list-inside list-disc">
              {allErrors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {hasSso && termsCheckbox}

        {hasSso && (
          <div className="grid gap-3">
            <SsoButtons ssoProviders={ssoProviders} termsAccepted={termsAccepted} />
          </div>
        )}

        {hasSso && (
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">{t("landing.login.or")}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <FieldGroup>
            <h2 className="text-lg font-semibold">{t("invitations.edit.title")}</h2>

            <Field>
              <Label>{t("invitations.edit.email")}</Label>
              <Input value={user.email} disabled />
            </Field>

            <Field>
              <Label htmlFor="invitation_password">{t("invitations.edit.password")}</Label>
              <Input
                id="invitation_password"
                type="password"
                autoComplete="new-password"
                value={form.data["user[password]"]}
                onChange={(e) => form.setData("user[password]", e.target.value)}
                required
              />
              <FieldDescription>{t("invitations.edit.password_help")}</FieldDescription>
            </Field>

            <Field>
              <Label htmlFor="invitation_password_confirmation">{t("invitations.edit.password_confirmation")}</Label>
              <Input
                id="invitation_password_confirmation"
                type="password"
                autoComplete="new-password"
                value={form.data["user[password_confirmation]"]}
                onChange={(e) => form.setData("user[password_confirmation]", e.target.value)}
                required
              />
            </Field>

            {!hasSso && termsCheckbox}

            <Button type="submit" className="w-full" disabled={!termsAccepted || form.processing}>
              {form.processing ? t("invitations.edit.submitting") : t("invitations.edit.submit")}
            </Button>
          </FieldGroup>
        </form>
      </div>
    </LandingPage>
  )
}

export default InvitationEditPage
